
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search, Star, Zap } from "lucide-react";
import { automationTemplates, getTemplatesByCategory, getPopularTemplates, AutomationTemplate } from '../data/automationTemplates';
import { AutomationRuleFormData } from '../schemas/automationRuleSchema';

interface AutomationTemplateSelectorProps {
  onSelectTemplate: (template: AutomationTemplate) => void;
  onClose: () => void;
}

export const AutomationTemplateSelector: React.FC<AutomationTemplateSelectorProps> = ({
  onSelectTemplate,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('popular');

  const filteredTemplates = React.useMemo(() => {
    let templates = selectedCategory === 'popular' 
      ? getPopularTemplates(6)
      : selectedCategory === 'all'
      ? automationTemplates
      : getTemplatesByCategory(selectedCategory);

    if (searchQuery) {
      templates = templates.filter(template =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return templates;
  }, [selectedCategory, searchQuery]);

  const categories = [
    { id: 'popular', name: 'Popular', icon: Star },
    { id: 'all', name: 'All Templates', icon: null },
    { id: 'communication', name: 'Communication', icon: null },
    { id: 'candidate_management', name: 'Candidate Management', icon: null },
    { id: 'follow_up', name: 'Follow-up', icon: null },
    { id: 'status_tracking', name: 'Status Tracking', icon: null },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Choose a Template</h2>
          <p className="text-muted-foreground">
            Start with a pre-built automation rule template
          </p>
        </div>
        <Button variant="outline" onClick={onClose}>
          Start from Scratch
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="grid w-full grid-cols-6">
          {categories.map((category) => (
            <TabsTrigger key={category.id} value={category.id} className="text-xs">
              {category.icon && <category.icon className="h-3 w-3 mr-1" />}
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={selectedCategory} className="mt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => onSelectTemplate(template)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{template.icon}</span>
                      <div>
                        <CardTitle className="text-sm font-medium">
                          {template.name}
                        </CardTitle>
                        {selectedCategory === 'popular' && (
                          <div className="flex items-center gap-1 mt-1">
                            <Zap className="h-3 w-3 text-yellow-500" />
                            <span className="text-xs text-yellow-600 font-medium">
                              {template.popularity}% popular
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {template.category.replace('_', ' ')}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    {template.description}
                  </p>
                  <div className="flex flex-wrap gap-1 text-xs">
                    <Badge variant="secondary">
                      {template.ruleData.trigger.type.replace('_', ' ')}
                    </Badge>
                    <Badge variant="secondary">
                      {template.ruleData.actions.length} action{template.ruleData.actions.length > 1 ? 's' : ''}
                    </Badge>
                    <Badge variant="secondary">
                      {template.ruleData.priority} priority
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No templates found matching your search.
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
